import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MsalHelperService } from '../services/msal-helper.service';

export type WorkspaceMemberRole = 'owner' | 'admin' | 'user';
export type WorkspaceMemberStatus = 'active' | 'pending';

export interface WorkspaceMember {
  rowKey: string;
  email: string;
  role: WorkspaceMemberRole;
  status: WorkspaceMemberStatus;
  external: boolean | null;
  disabled: boolean;
  addedTime?: string;
  addedBy?: string;
  allowedOutsideCowork: boolean;
  hasActiveKey?: boolean;
}

export interface WorkspaceMemberListResponse {
  workspaceId: string;
  plan: string;
  pooledLimit: number;
  perMemberLimit: number;
  used: number | null;
  usedAsOf: string | null;
  usedPeriodStart: string | null;
  seatsUsed: number;
  callerRole: WorkspaceMemberRole;
  members: WorkspaceMember[];
}

export interface AddWorkspaceMemberRequest {
  workspaceId: string;
  email: string;
  role: Exclude<WorkspaceMemberRole, 'owner'>;
  allowedOutsideCowork: boolean;
}

export class WorkspaceMemberRequestError extends Error {
  constructor(public readonly status: number, public readonly code: string) {
    super(code);
  }
}

@Injectable({ providedIn: 'root' })
export class TeamsWorkspaceMembersService {
  private readonly scopes = ['https://service.flow.microsoft.com//Flows.Manage.All'];

  constructor(private msalHelperService: MsalHelperService) {}

  public async list(workspaceId?: string): Promise<WorkspaceMemberListResponse> {
    const query = workspaceId ? `?workspaceId=${encodeURIComponent(workspaceId)}` : '';
    return this.request<WorkspaceMemberListResponse>(`/api/workspace-members${query}`, 'GET');
  }

  public add(request: AddWorkspaceMemberRequest): Promise<WorkspaceMember> {
    return this.request<WorkspaceMember>('/api/workspace-members/add', 'POST', request);
  }

  public remove(workspaceId: string, rowKey: string): Promise<{ ok: boolean; removed: boolean }> {
    return this.request('/api/workspace-members/remove', 'POST', { workspaceId, rowKey });
  }

  public setRole(
    workspaceId: string,
    rowKey: string,
    role: Exclude<WorkspaceMemberRole, 'owner'>
  ): Promise<{ ok: boolean; rowKey: string; role: WorkspaceMemberRole }> {
    return this.request('/api/workspace-members/set-role', 'POST', { workspaceId, rowKey, role });
  }

  public setAccess(
    workspaceId: string,
    rowKey: string,
    allowedOutsideCowork: boolean
  ): Promise<{ ok: boolean; rowKey: string; allowedOutsideCowork: boolean }> {
    return this.request('/api/workspace-members/set-access', 'POST', {
      workspaceId,
      rowKey,
      allowedOutsideCowork
    });
  }

  private async request<T>(url: string, method: 'GET' | 'POST', body?: unknown): Promise<T> {
    const token = await this.getTokenWithRefreshFallback();
    const accessToken = token?.accessToken;
    if (!accessToken) {
      throw new WorkspaceMemberRequestError(401, 'authentication_required');
    }

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'x-authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new WorkspaceMemberRequestError(response.status, result?.error || 'request_failed');
    }
    return result as T;
  }

  private async getTokenWithRefreshFallback(): Promise<{ accessToken: string } | null> {
    try {
      return await firstValueFrom(this.msalHelperService.getAccessTokenSilent$(this.scopes));
    } catch (firstError) {
      try {
        return await firstValueFrom(this.msalHelperService.getAccessTokenSilent$(this.scopes, true));
      } catch (refreshError) {
        this.msalHelperService.requestReauthPrompt(refreshError || firstError, this.scopes);
        return null;
      }
    }
  }
}
